import React, { useCallback, useContext, useMemo } from 'react';
import { defineMessages, useIntl, type MessageDescriptor, FormattedMessage } from 'react-intl';
import uniqBy from 'lodash/uniqBy';

import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left';
import CommentIcon from '@atlaskit/icon/core/comment';
import EditIcon from '@atlaskit/icon/core/edit';
import PersonIcon from '@atlaskit/icon/core/person';
import Lozenge from '@atlaskit/lozenge/lozenge';

import type {
	FilterConfig,
	ObjectListFlyoutHookParams,
	ObjectListFlyoutHookResults,
	ObjectListFlyoutItem,
} from '@atlassian/navigation-common/types';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { formatTimeAgo } from '@confluence/time-ago';
import { shouldUseAGGActivityV3 } from '@confluence/home/entry-points/shouldUseAGGActivityV3.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { ContentIcon } from '@confluence/icons/entry-points/ContentIcon.ts';
import { APP_NAV_PAGES_DROPDOWN_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';

import {
	getQueryOptions,
	type FilterType,
	type TransformedActivityNode,
} from '../../ContentMenu/recentQueryConfig.ts';

import { getContentEmojiAndTitle, i18n as titleMessages } from './getContentEmojiandTitle.ts';

type RecentSection = {
	title: MessageDescriptor;
	daysAgo: number;
	nodes: TransformedActivityNode[];
	sectionId: string;
};

type UseRecentDataParams = {
	searchText?: string;
	selectedFilter?: FilterType;
	onCompleted?: () => void;
	onError?: () => void;
};

export const useRecentItems = (
	params: ObjectListFlyoutHookParams,
): ObjectListFlyoutHookResults<ObjectListFlyoutItem> => {
	const intl = useIntl();
	const selectedFilter: FilterType = (params.selectedFilters?.[0] as FilterType) || 'ALL';
	const experienceTracker = useContext(ExperienceTrackerContext);

	const {
		data,
		loading,
		error,
		isUnauthorized,
		sections: rawSections,
		hasNextPage,
		loadMore,
	} = useRecentData({
		searchText: params.searchText,
		selectedFilter,
	});

	const sections = useMemo(() => {
		if (error) {
			return [];
		}

		const result: Array<{ header: string; items: ObjectListFlyoutItem[] }> = [];

		for (const section of rawSections) {
			if (section.nodes.length === 0) {
				continue;
			}

			const items: ObjectListFlyoutItem[] = [];
			// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
			for (const node of section.nodes) {
				const content = node?.object?.content;
				const url = node?.object?.url || '';

				const { emoji, title } = content
					? getContentEmojiAndTitle(content)
					: { emoji: undefined, title: undefined };
				const isDraft = content?.status === 'draft';

				items.push({
					elemBefore: content?.type ? (
						<ContentIcon type={content.type} subType={content?.subType} emoji={emoji} />
					) : null,
					elemAfter: isDraft && (
						<Lozenge appearance="default">
							<FormattedMessage
								{...(fg('confluence_lozenge_text_update')
									? i18n.draftLozengeTextUpdated
									: i18n.draftLozengeText)}
							/>
						</Lozenge>
					),
					primaryLabel: title || intl.formatMessage(titleMessages.untitled),
					secondaryLabel: content?.space?.name || '',
					tertiaryLabel: node?.timestamp ? formatTimeAgo(intl, node?.timestamp) : undefined,
					href: url,
					extendedSubItemAnalyticsAttributes: content
						? {
								componentType: 'customItem',
								contentType: content.type ?? null,
								contentId: content.id ?? null,
								contentStatus: content.status ?? null,
								section: intl.formatMessage(section.title),
								selectedFilter,
								isLivePage: content.subType === 'live',
							}
						: undefined,
				});
			}

			result.push({ header: intl.formatMessage(section.title), items });
		}

		return result;
	}, [rawSections, error, intl, selectedFilter]);

	const returnError = useMemo(() => {
		if (isUnauthorized) {
			return new Error(intl.formatMessage(i18n.authErrorMessageTitle));
		}
		if (error) {
			experienceTracker.stopOnError({
				name: APP_NAV_PAGES_DROPDOWN_EXPERIENCE,
				error,
				attributes: { navVersion: '4', dropdownType: 'recent' },
			});
		}
		return error;
	}, [error, isUnauthorized, intl, experienceTracker]);

	return {
		sections,
		hasMore: hasNextPage,
		isLoading: loading && !data,
		loadMore,
		error: returnError,
	};
};

export const useRecentData = (params: UseRecentDataParams) => {
	const { cloudId } = useSessionData();
	const queryOptions = getQueryOptions(cloudId);
	const selectedFilter: FilterType = params.selectedFilter || 'ALL';
	const searchText = params.searchText || '';

	const { data, loading, error, fetchMore } = useQueryInterop(queryOptions[selectedFilter].query, {
		variables: queryOptions[selectedFilter].initialVars,
		fetchPolicy: 'cache-and-network',
		interopApolloAggClient: shouldUseAGGActivityV3(),
		onCompleted: params.onCompleted,
		onError: params.onError,
	});

	const isUnauthorized = !!(error && isUnauthorizedError(error));

	const filteredNodes = useMemo(() => {
		if (error) {
			if (isUnauthorizedError(error)) {
				markErrorAsHandled(error);
			}
			return [];
		}

		const lowerFilterText = searchText.toLowerCase();
		const nodes: TransformedActivityNode[] = queryOptions[selectedFilter].transformNodes(data);

		return (
			uniqBy(
				// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
				nodes
					?.filter(
						(node) =>
							!searchText || node?.object?.content?.title?.toLowerCase().includes(lowerFilterText),
					)
					.filter((node) => node?.object?.content?.status !== 'archived')
					.filter((node) => node?.object?.content?.status !== 'trashed'),
				(node: any) => node?.object?.content?.id,
			) ?? []
		);
	}, [data, error, queryOptions, searchText, selectedFilter]);

	const sections = useMemo(() => groupNodesIntoSections(filteredNodes), [filteredNodes]);

	const { endCursor, hasNextPage } = queryOptions[selectedFilter].getPaginationParams(data);

	const loadMore = useCallback((): void => {
		if (hasNextPage && endCursor && !loading) {
			void fetchMore({
				variables: queryOptions[selectedFilter].fetchMoreVars(endCursor),
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult) {
						return prev;
					}
					return queryOptions[selectedFilter].updatePaginationResults(prev, fetchMoreResult);
				},
			});
		}
	}, [hasNextPage, endCursor, loading, fetchMore, queryOptions, selectedFilter]);

	return {
		data,
		loading,
		error,
		isUnauthorized,
		filteredNodes,
		sections,
		hasNextPage,
		loadMore,
	};
};

export const useRecentsFilterConfig = (): FilterConfig => {
	const intl = useIntl();
	return {
		defaultSelected: ['ALL'],
		filters: [
			{
				key: 'ALL',
				label: intl.formatMessage(i18n.all),
				elemBefore: <AlignTextLeftIcon label="" />,
			},
			{
				key: 'COLLABORATED_ON',
				label: intl.formatMessage(i18n.collaboratedOn),
				elemBefore: <CommentIcon label="" />,
			},
			{
				key: 'WORKED_ON',
				label: intl.formatMessage(i18n.workedOn),
				elemBefore: <EditIcon label="" />,
			},
			{
				key: 'CREATED_BY_ME',
				label: intl.formatMessage(i18n.ownedByMe),
				elemBefore: <PersonIcon label="" />,
			},
		],
		hideSelectedFilterTag: ['ALL'],
	};
};

export const groupNodesIntoSections = (nodes: TransformedActivityNode[] | undefined) => {
	const sections: RecentSection[] = [
		{ title: i18n.today, daysAgo: 0, nodes: [], sectionId: 'today' },
		{ title: i18n.yesterday, daysAgo: 1, nodes: [], sectionId: 'yesterday' },
		{ title: i18n.lastWeek, daysAgo: 7, nodes: [], sectionId: 'lastWeek' },
		{ title: i18n.lastMonth, daysAgo: 30, nodes: [], sectionId: 'lastMonth' },
		{ title: i18n.moreThanMonth, daysAgo: -1, nodes: [], sectionId: 'moreThanMonth' },
	];

	let sectionIndex = 0;
	const startOfToday = new Date().setHours(0, 0, 0, 0);

	nodes?.forEach((node) => {
		if (!node?.timestamp) {
			sections[4].nodes.push(node);
			return;
		}

		const nodeDate = new Date(node.timestamp).getTime();
		const daysDiff = Math.floor((startOfToday - nodeDate) / (1000 * 60 * 60 * 24));
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
		while (sectionIndex < sections.length - 1 && daysDiff >= sections[sectionIndex].daysAgo) {
			sectionIndex++;
		}

		sections[sectionIndex].nodes.push(node);
	});

	return sections;
};

export const i18n = defineMessages({
	today: {
		id: 'side-navigation.recent-content-menu.section.today',
		defaultMessage: 'Today',
		description: 'Section heading for content that was accessed today',
	},
	yesterday: {
		id: 'side-navigation.recent-content-menu.section.yesterday',
		defaultMessage: 'Yesterday',
		description: 'Section heading for content that was accessed on the day before today',
	},
	lastWeek: {
		id: 'side-navigation.recent-content-menu.section.last-week',
		defaultMessage: 'In the last week',
		description: 'Section heading for content that was accessed in the last week',
	},
	lastMonth: {
		id: 'side-navigation.recent-content-menu.section.last-month',
		defaultMessage: 'In the last month',
		description: 'Section heading for content that was accessed in the last month',
	},
	moreThanMonth: {
		id: 'side-navigation.recent-content-menu.section.more-than-month',
		defaultMessage: 'More than a month ago',
		description: 'Section heading for content that was accessed more than one month ago',
	},
	authErrorMessageTitle: {
		id: 'side-navigation.recent-content-menu.auth-error-message.title',
		defaultMessage: 'You are not authorized to load recent items',
		description: 'Title for the authentication error message in the recent content menu',
	},
	all: {
		id: 'side-navigation.recent-content-menu.filter.all',
		defaultMessage: 'All',
		description: 'Name of a filter to display all recent items',
	},
	collaboratedOn: {
		id: 'side-navigation.recent-content-menu.filter.collaborated-on',
		defaultMessage: 'Collaborated on',
		description: 'Name of a filter to display content recently collaborated on',
	},
	workedOn: {
		id: 'side-navigation.recent-content-menu.filter.worked-on',
		defaultMessage: 'Worked on',
		description: 'Name of a filter to display content recently worked on',
	},
	ownedByMe: {
		id: 'side-navigation.recent-content-menu.filter.owned-by-me',
		defaultMessage: 'Owned by me',
		description: 'Name of a filter to display content owned by user',
	},
	draftLozengeText: {
		id: 'side-navigation.content-menu.lozenge.draft',
		defaultMessage: 'DRAFT',
		description: 'Lozenge shown in global navigation content menu when the content is a draft',
	},
	draftLozengeTextUpdated: {
		id: 'side-navigation.content-menu.lozenge.draft.updated',
		defaultMessage: 'Draft',
		description: 'Lozenge shown in global navigation content menu when the content is a draft',
	},
});
